(() => {
  const STORAGE_PREFIX = "dropsafe:form-prefill:";
  const DRAFT_SUFFIX = ":draft";
  const INTENT_SUFFIX = ":multi";
  const MAX_AGE_MS = 30 * 60 * 1000;
  const BOUNCE_WINDOW_MS = 2 * 60 * 1000;
  const PREEMPT_DELAY_MS = 150;
  const SUBMIT_CLICK_WINDOW_MS = 15 * 1000;
  const RENDER_WAIT_MS = 20000;
  const APPLY_MAX_PASSES = 8;
  const APPLY_PASS_DELAY_MS = 300;

  const config = window.formPrefillConfig || {};
  const formIds = (config.formIds || []).map((id) => String(id).trim()).filter(Boolean);

  // Locale strings arrive HTML-escaped (Shopify escapes `| t` output), but we
  // render them via textContent, so decode entities like &#39; back to text.
  function decodeEntities(value) {
    if (String(value).indexOf("&") === -1) return String(value);
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  }

  const strings = {};
  Object.keys(window.formPrefillStrings || {}).forEach((key) => {
    strings[key] = decodeEntities(window.formPrefillStrings[key]);
  });

  // Per-person fields are cleared after a refill so every registration gets
  // its own name + email; everything else (company, address, phone, training
  // choices) is reused as-is.
  const personalPatterns = config.personalFieldPatterns || [
    /first\s*name/i,
    /last\s*name/i,
    /e-?mail/i,
    /sobrenome/i,
  ];

  if (!formIds.length) return;
  if (!strings.multiLabel || !strings.applied) return;

  const DEBUG = (() => {
    try {
      return /[?&#]prefill_debug/.test(window.location.href) || localStorage.getItem(STORAGE_PREFIX + "debug") === "1";
    } catch (e) {
      return false;
    }
  })();
  const debug = (...args) => {
    if (DEBUG) console.log("[form-prefill]", ...args);
  };

  let storageOk = false;
  try {
    const probe = STORAGE_PREFIX + "probe";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    storageOk = true;
  } catch (e) {
    storageOk = false;
  }
  if (!storageOk) return;

  const savedKeyFor = (formId) => STORAGE_PREFIX + formId;
  const draftKeyFor = (formId) => STORAGE_PREFIX + formId + DRAFT_SUFFIX;
  const intentKeyFor = (formId) => STORAGE_PREFIX + formId + INTENT_SUFFIX;

  function readSaved(formId) {
    try {
      const raw = localStorage.getItem(savedKeyFor(formId));
      if (!raw) return null;
      const data = JSON.parse(raw);
      const stale = !data || !data.fields || Date.now() - (data.savedAt || 0) > MAX_AGE_MS;
      if (stale) {
        localStorage.removeItem(savedKeyFor(formId));
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeJson(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage full — feature degrades silently */
    }
  }

  const readIntent = (formId) => localStorage.getItem(intentKeyFor(formId)) === "1";

  function writeIntent(formId, on) {
    if (on) {
      localStorage.setItem(intentKeyFor(formId), "1");
    } else {
      localStorage.removeItem(intentKeyFor(formId));
    }
  }

  function clearStored(formId) {
    localStorage.removeItem(savedKeyFor(formId));
    localStorage.removeItem(draftKeyFor(formId));
  }

  function isCapturable(el) {
    if (!el.name) return false;
    const type = (el.type || "").toLowerCase();
    if (["password", "file", "hidden", "submit", "button", "reset", "image"].indexOf(type) !== -1) return false;
    const identity = (el.name + " " + el.id).toLowerCase();
    if (identity.indexOf("captcha") !== -1 || identity.indexOf("token") !== -1) return false;
    if (el.closest("[data-form-prefill-panel]")) return false;
    return true;
  }

  function fieldsIn(container) {
    const all = container.querySelectorAll("input, select, textarea");
    return Array.prototype.filter.call(all, isCapturable);
  }

  function fieldKey(el) {
    if (el.type === "radio" || el.type === "checkbox") return el.name + "::" + el.value;
    return el.name;
  }

  function labelTextFor(el) {
    const wrapper = el.closest(".globo-form-input");
    const scope = wrapper && wrapper.parentElement ? wrapper.parentElement : null;
    const label = (scope && scope.querySelector("label")) || null;
    return label ? label.textContent || "" : "";
  }

  const normalize = (text) => String(text || "").toLowerCase().replace(/\s+/g, " ").trim();

  // Secondary identity for a field, independent of its name attribute. Used to
  // re-match saved values when the embed regenerates names between page loads.
  function signature(el) {
    const parts = [
      (el.type || el.tagName).toLowerCase(),
      normalize(el.placeholder),
      normalize(labelTextFor(el)),
    ];
    if (el.type === "radio" || el.type === "checkbox") parts.push(normalize(el.value));
    return parts.join("|");
  }

  // Labels that identify a person only when they are the whole field label —
  // "Nome" is the pt-BR first name, but "Nome da empresa" is the company.
  const exactPersonalPatterns = [/^nome\s*\*?$/i];

  function isPersonalField(el) {
    const hay = normalize([el.name, el.id, el.placeholder, labelTextFor(el)].join(" "));
    if (personalPatterns.some((pattern) => pattern.test(hay))) return true;
    const pieces = [normalize(el.placeholder), normalize(labelTextFor(el))];
    return exactPersonalPatterns.some((pattern) => pieces.some((piece) => piece && pattern.test(piece)));
  }

  function snapshot(container) {
    const fields = {};
    fieldsIn(container).forEach((el) => {
      const entry = { sig: signature(el) };
      if (el.type === "radio" || el.type === "checkbox") {
        entry.kind = "check";
        entry.checked = el.checked;
      } else {
        entry.kind = "value";
        entry.value = el.value;
      }
      fields[fieldKey(el)] = entry;
    });
    return fields;
  }

  function hasContent(fields) {
    return Object.keys(fields).some((key) => {
      const f = fields[key];
      return f.kind === "check" ? f.checked : f.value !== "";
    });
  }

  function fireEvents(el) {
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function applyTo(el, saved) {
    if (saved.kind === "check") {
      if (el.checked !== saved.checked) {
        el.checked = saved.checked;
        fireEvents(el);
      }
    } else if (saved.value && el.value !== saved.value) {
      el.value = saved.value;
      fireEvents(el);
    }
  }

  // One refill pass: apply every saved value whose field currently exists in
  // the DOM — first by name, then by signature for renamed fields. Conditional
  // fields (e.g. securing-device options that only render after a product is
  // picked) appear in response to the change events we dispatch, so applyAll
  // re-runs passes until a pass matches nothing new.
  function applyOnce(container, fields, appliedKeys, appliedEls) {
    let progressed = false;
    const domFields = fieldsIn(container).filter((el) => !appliedEls.has(el));

    domFields.forEach((el) => {
      const key = fieldKey(el);
      if (appliedKeys.has(key) || !fields[key]) return;
      applyTo(el, fields[key]);
      appliedKeys.add(key);
      appliedEls.add(el);
      progressed = true;
    });

    const remainingKeys = Object.keys(fields).filter((key) => !appliedKeys.has(key));
    if (remainingKeys.length) {
      const elsBySig = {};
      domFields.forEach((el) => {
        if (appliedEls.has(el)) return;
        const sig = signature(el);
        (elsBySig[sig] = elsBySig[sig] || []).push(el);
      });
      const keysBySig = {};
      remainingKeys.forEach((key) => {
        const sig = fields[key].sig;
        if (sig) (keysBySig[sig] = keysBySig[sig] || []).push(key);
      });
      Object.keys(keysBySig).forEach((sig) => {
        const els = elsBySig[sig] || [];
        const keys = keysBySig[sig];
        if (els.length === 1 && keys.length === 1) {
          applyTo(els[0], fields[keys[0]]);
          appliedKeys.add(keys[0]);
          appliedEls.add(els[0]);
          progressed = true;
        }
      });
    }
    return progressed;
  }

  function applyAll(container, fields, done) {
    const appliedKeys = new Set();
    const appliedEls = new Set();
    let pass = 0;
    const run = () => {
      pass += 1;
      const progressed = applyOnce(container, fields, appliedKeys, appliedEls);
      if (progressed && pass < APPLY_MAX_PASSES) {
        setTimeout(run, APPLY_PASS_DELAY_MS);
      } else {
        debug("refill finished:", appliedKeys.size, "of", Object.keys(fields).length, "saved fields applied in", pass, "passes");
        done(appliedKeys.size);
      }
    };
    run();
  }

  function clearPersonalFields(container) {
    let firstCleared = null;
    fieldsIn(container).forEach((el) => {
      if (el.type === "radio" || el.type === "checkbox" || el.tagName === "SELECT") return;
      if (!isPersonalField(el)) return;
      if (el.value !== "") {
        el.value = "";
        fireEvents(el);
      }
      if (!firstCleared) firstCleared = el;
    });
    if (firstCleared) {
      firstCleared.scrollIntoView({ behavior: "smooth", block: "center" });
      firstCleared.focus({ preventScroll: true });
    }
  }

  // The panel gets its own injected stylesheet: the theme's utility CSS is
  // compiled from classes used in Liquid source, so classes referenced only
  // from JS may not exist in style.css. Styling follows the theme's language —
  // sharp corners, theme yellow, black checkmark (cf. the b2b-screen benefit
  // checkmarks).
  const STYLE_ID = "ds-form-prefill-styles";
  const CHECKMARK_SVG =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' d='M2.5 8.5l3.5 3.5 7-7'/%3E%3C/svg%3E\")";

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = [
      ".ds-form-prefill-toggle{display:flex;align-items:center;gap:12px;cursor:pointer;font-size:14px;line-height:1.4;}",
      ".ds-form-prefill-toggle input{appearance:none;-webkit-appearance:none;margin:0;flex-shrink:0;width:20px;height:20px;",
      "border:1px solid rgba(255,255,255,.4);background-color:rgba(255,255,255,.1);cursor:pointer;display:inline-grid;place-content:center;",
      "transition:background-color .15s ease,border-color .15s ease;}",
      ".ds-form-prefill-toggle input:hover{border-color:#fff;}",
      ".ds-form-prefill-toggle input:focus-visible{outline:2px solid #FBD500;outline-offset:2px;}",
      ".ds-form-prefill-toggle input:checked{background-color:#FBD500;border-color:#FBD500;}",
      ".ds-form-prefill-toggle input:checked::after{content:'';width:12px;height:12px;",
      "background:" + CHECKMARK_SVG + " center/contain no-repeat;}",
      ".ds-form-prefill-status{margin-top:8px;padding-left:32px;font-size:13px;opacity:.9;}",
    ].join("");
    document.head.appendChild(style);
  }

  function buildPanel(container, formId) {
    ensureStyles();
    const panel = document.createElement("div");
    panel.setAttribute("data-form-prefill-panel", formId);
    panel.style.maxWidth = "600px";
    panel.style.margin = "16px auto 0";

    const toggleLabel = document.createElement("label");
    toggleLabel.className = "ds-form-prefill-toggle";

    const toggle = document.createElement("input");
    toggle.type = "checkbox";
    toggle.checked = readIntent(formId);

    const toggleText = document.createElement("span");
    toggleText.textContent = strings.multiLabel;

    const status = document.createElement("p");
    status.className = "ds-form-prefill-status";
    status.setAttribute("aria-live", "polite");
    status.hidden = true;

    toggle.addEventListener("change", () => {
      writeIntent(formId, toggle.checked);
      if (!toggle.checked) {
        clearStored(formId);
        status.hidden = true;
        debug("multi-user mode off — cleared saved data for", formId);
      } else {
        debug("multi-user mode on for", formId);
        saveDraftNow(container, formId);
      }
    });

    toggleLabel.appendChild(toggle);
    toggleLabel.appendChild(toggleText);
    panel.appendChild(toggleLabel);
    panel.appendChild(status);
    container.parentElement.insertBefore(panel, container.nextSibling);

    maybeAutoRefill(container, formId, status);
  }

  function maybeAutoRefill(container, formId, status) {
    if (!readIntent(formId)) return;
    const saved = readSaved(formId);
    if (!saved) return;
    debug("auto-refilling", formId, "from submission at", new Date(saved.savedAt).toISOString());
    // The refill consumes the bounce: once the registrant is back on the
    // form, leaving the page again must never pull them back.
    if (!saved.bounced) {
      saved.bounced = true;
      writeJson(savedKeyFor(formId), saved);
    }
    applyAll(container, saved.fields, (appliedCount) => {
      clearPersonalFields(container);
      // Refill events are programmatic; only real typing afterwards should
      // count as a new registration in progress.
      container.__prefillUserEdited = false;
      if (appliedCount) {
        status.textContent = strings.applied;
        status.hidden = false;
      }
    });
  }

  // The app controls the post-submit redirect and JS cannot cancel another
  // script's navigation, so ride it instead: when the redirect target loads
  // with multi-user mode on and a fresh submission stored, immediately return
  // to the form page, where maybeAutoRefill takes over.
  function maybeBounceBack() {
    for (const formId of formIds) {
      const saved = readSaved(formId);
      if (!saved || !saved.returnUrl || saved.bounced) continue;
      if (!readIntent(formId)) continue;
      if (Date.now() - (saved.savedAt || 0) > BOUNCE_WINDOW_MS) continue;
      if (saved.returnUrl.split("?")[0] === window.location.pathname) continue;
      saved.bounced = true;
      writeJson(savedKeyFor(formId), saved);
      debug("bouncing back to", saved.returnUrl);
      window.location.replace(saved.returnUrl);
      return true;
    }
    return false;
  }

  function debounce(fn, wait) {
    let timer = null;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  // Drafts hold personal data, so they exist only after the visitor opts into
  // multi-user mode — without the tick nothing downstream ever reads them.
  function saveDraftNow(container, formId) {
    if (!readIntent(formId)) return;
    const fields = snapshot(container);
    if (hasContent(fields)) {
      writeJson(draftKeyFor(formId), { savedAt: Date.now(), fields });
    }
  }

  // Remove expired drafts/saved records on every page load, so data from an
  // abandoned form never outlives the freshness window.
  function sweepExpired() {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || key.indexOf(STORAGE_PREFIX) !== 0) continue;
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (data && typeof data === "object" && data.savedAt && Date.now() - data.savedAt > MAX_AGE_MS) {
          doomed.push(key);
        }
      } catch (e) {
        /* intent/debug flags — not JSON records, leave them */
      }
    }
    doomed.forEach((key) => localStorage.removeItem(key));
    if (doomed.length) debug("swept", doomed.length, "expired record(s)");
  }

  function promoteDraft(formId) {
    try {
      const raw = localStorage.getItem(draftKeyFor(formId));
      if (!raw) return;
      const draft = JSON.parse(raw);
      const fresh = draft && Date.now() - (draft.savedAt || 0) <= MAX_AGE_MS;
      if (fresh && draft.fields && hasContent(draft.fields)) {
        writeJson(savedKeyFor(formId), {
          savedAt: Date.now(),
          fields: draft.fields,
          returnUrl: window.location.pathname + window.location.search,
          bounced: false,
        });
        debug("promoted draft for", formId, "-", Object.keys(draft.fields).length, "fields");
      }
    } catch (e) {
      /* corrupt draft — ignore */
    }
    localStorage.removeItem(draftKeyFor(formId));
  }

  let lastSubmitClickAt = 0;

  // App-agnostic success detection: on a successful submission the embed
  // clears its fields (visible in both the popup and redirect configurations).
  // A filled form dropping to zero filled fields is treated as "submitted" —
  // no dependence on the app's events, button markup, or navigation. The
  // draft still holds the pre-clear values (empty snapshots never overwrite
  // it), so promoting here saves the submitted data.
  function startClearWatcher(container, formId) {
    let lastCount = -1;
    setInterval(() => {
      let count = 0;
      fieldsIn(container).forEach((el) => {
        if (el.tagName === "SELECT") return;
        if (el.type === "radio" || el.type === "checkbox") {
          if (el.checked) count += 1;
        } else if (el.value !== "") {
          count += 1;
        }
      });
      if (lastCount >= 2 && count === 0) {
        debug("form cleared — treating as successful submission");
        promoteDraft(formId);
        if (readIntent(formId)) {
          setTimeout(() => {
            debug("multi-user mode: refreshing for the next registration");
            window.location.replace(window.location.pathname + window.location.search);
          }, PREEMPT_DELAY_MS);
        }
      }
      lastCount = count;
    }, 500);
  }

  function setupContainer(container, formId) {
    debug("form ready:", formId, "-", fieldsIn(container).length, "capturable fields");
    container.__prefillUserEdited = true;
    const saveDraft = debounce(() => saveDraftNow(container, formId), 300);
    container.addEventListener("input", saveDraft);
    container.addEventListener("change", saveDraft);
    const markEdited = (event) => {
      if (event.isTrusted) container.__prefillUserEdited = true;
    };
    container.addEventListener("input", markEdited, true);
    container.addEventListener("change", markEdited, true);

    // Globo may redirect the instant the form submits; flush the draft on any
    // submit-looking click so the pagehide fallback below always has current
    // data. The submit control may not be a real <button>, so match broadly —
    // a spurious match only causes a harmless extra draft save. Capture phase,
    // in case the embed stops propagation.
    const submitish = 'button, input[type="submit"], input[type="button"], a, [role="button"], [class*="submit"], [class*="btn"]';
    container.addEventListener(
      "click",
      (event) => {
        const target = event.target && event.target.closest ? event.target.closest(submitish) : null;
        if (target) {
          lastSubmitClickAt = Date.now();
          saveDraftNow(container, formId);
        }
      },
      true
    );

    // If the embed uses a real form submission, this is the strongest signal:
    // promote right away (never navigate here — the submission itself may
    // still be in flight).
    container.addEventListener(
      "submit",
      () => {
        debug("native submit event on", formId);
        lastSubmitClickAt = Date.now();
        saveDraftNow(container, formId);
        promoteDraft(formId);
      },
      true
    );

    startClearWatcher(container, formId);

    if (!document.querySelector('[data-form-prefill-panel="' + formId + '"]')) {
      buildPanel(container, formId);
    }
  }

  // The Globo embed renders its fields asynchronously into the container, so
  // wait until inputs exist before wiring anything up.
  function whenRendered(container, callback) {
    if (fieldsIn(container).length) {
      callback();
      return;
    }
    const started = Date.now();
    const observer = new MutationObserver(() => {
      if (fieldsIn(container).length) {
        observer.disconnect();
        callback();
      } else if (Date.now() - started > RENDER_WAIT_MS) {
        observer.disconnect();
        debug("gave up waiting for fields in", container.getAttribute("data-id"));
      }
    });
    observer.observe(container, { childList: true, subtree: true });
  }

  function enabledContainers() {
    const containers = document.querySelectorAll(".globo-formbuilder[data-id]");
    return Array.prototype.filter.call(containers, (el) => formIds.indexOf(el.getAttribute("data-id")) !== -1);
  }

  function promoteAllDrafts() {
    enabledContainers().forEach((container) => {
      promoteDraft(container.getAttribute("data-id"));
    });
  }

  document.addEventListener("globo.formbuilder.form.submitted", () => {
    debug("globo.formbuilder.form.submitted received");
    promoteAllDrafts();
    // Multi-user mode: try to preempt the app's configured redirect with an
    // in-place refresh (the submission has already completed at this point).
    // If the app's navigation wins the race anyway, maybeBounceBack recovers
    // from whatever page it lands on. Single-user mode changes nothing.
    const anyIntent = enabledContainers().some((c) => readIntent(c.getAttribute("data-id")));
    if (anyIntent) {
      setTimeout(() => {
        debug("attempting in-place refresh to preempt the redirect");
        window.location.replace(window.location.pathname + window.location.search);
      }, PREEMPT_DELAY_MS);
    }
  });

  // Fallback for redirects that outrun the submitted event — or embeds where
  // that event never fires: promote the draft as the page is torn down. With
  // the multi-user box ticked, the tick itself is the signal, so no
  // submit-button detection is needed at all; otherwise require a recent
  // submit-looking click.
  window.addEventListener("pagehide", () => {
    // Promote only when the departure looks like a submission — a recent
    // submit-looking click on a form the user actually edited. Leaving an
    // edited form via ordinary navigation must never arm the bounce-back.
    if (Date.now() - lastSubmitClickAt >= SUBMIT_CLICK_WINDOW_MS) return;
    enabledContainers().forEach((container) => {
      if (container.__prefillUserEdited !== false) {
        promoteDraft(container.getAttribute("data-id"));
      }
    });
  });

  const init = () => {
    sweepExpired();
    if (maybeBounceBack()) return;
    const containers = enabledContainers();
    debug("init:", containers.length, "enabled form container(s) on this page");
    containers.forEach((container) => {
      whenRendered(container, () => setupContainer(container, container.getAttribute("data-id")));
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
